// src/components/Auth/Signup.js

import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import WaitlistForm from './WaitlistForm';
import api from '../../api/axiosConfig';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Google as GoogleIcon,
  ArrowBack as ArrowBackIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeContext';

function Signup() {
  const theme = useTheme();
  const { darkMode, toggleDarkMode } = useThemeMode();
  const emailRef = useRef();
  const passwordRef = useRef();
  const passwordConfirmRef = useRef();
  const { signup, loginWithGoogle, accessDenied, clearAccessDenied, handleInviteSuccess } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupInfo, setSignupInfo] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    // Fetch signup info on component mount
    const fetchSignupInfo = async () => {
      try {
        const response = await api.get('/api/auth/signup-info');
        setSignupInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch signup info:', error);
      }
    };
    fetchSignupInfo();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    if (passwordRef.current.value !== passwordConfirmRef.current.value) {
      return setError('Passwords do not match');
    }

    if (!acceptedTerms) {
      return setError('You must accept the Terms of Service and Privacy Policy to create an account');
    }

    try {
      setError('');
      setLoading(true);
      const result = await signup(emailRef.current.value, passwordRef.current.value);

      if (result?.emailVerificationSent) {
        // Show success message for email verification
        setError(''); // Clear any errors
        alert('✅ Account created! Please check your email and click the verification link before signing in.');
        navigate('/login'); // Redirect to login page
        setLoading(false);
        return;
      }

      if (result?.accessDenied) {
        // Access denied - waitlist form will be shown by accessDenied state
        setLoading(false);
        return;
      }

      // This shouldn't happen with new flow, but keep as fallback
      navigate('/sessions');
    } catch (error) {
      setError(error.message || 'Failed to create an account');
    }

    setLoading(false);
  }

  const handleGoogleSignup = async () => {
    // CRITICAL: Check terms acceptance before Google signup
    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy to create an account');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const result = await loginWithGoogle(); // Google signup is same as login
      if (result?.accessDenied) {
        // Access denied - waitlist form will be shown by accessDenied state
        setLoading(false);
        return;
      }
      navigate('/sessions');
    } catch (error) {
      console.error('Google signup failed:', error);

      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-up was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups and try again');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google Sign-Up is not enabled. Please contact support.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google Sign-Up');
      } else {
        setError(`Failed to sign up with Google: ${error.message}`);
      }
    }
    setLoading(false);
  };

  const onInviteSuccess = async (inviteData) => {
    try {
      const result = await handleInviteSuccess(inviteData);
      if (result?.success) {
        navigate('/sessions');
      }
    } catch (error) {
      setError('Failed to process invite. Please try signing up again.');
    }
  };

  // Show waitlist form if access is denied
  if (accessDenied) {
    return (
      <WaitlistForm
        error={accessDenied.error}
        email={accessDenied.email}
        allowedDomains={accessDenied.allowedDomains || signupInfo?.allowedDomains || []}
        onInviteSuccess={onInviteSuccess}
        onClose={clearAccessDenied}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign Up - Blackmount</title>
        <meta name="description" content="Create your free Blackmount account and start saving your browser sessions, notes, and research across all devices." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Top Navigation */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/"
          sx={{ color: 'text.secondary' }}
        >
          Back to Blackmount
        </Button>
        
        <IconButton onClick={toggleDarkMode} aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
          {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Box>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card 
          elevation={0}
          sx={{ 
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  component="img"
                  src="/blackmount-logo.jpg"
                  alt="Blackmount"
                  sx={{
                    height: '40px',
                    width: 'auto',
                    borderRadius: 1
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                component="h1"
                fontWeight={700}
                sx={{
                  mb: 1,
                  background: 'linear-gradient(45deg, #2563eb, #7c3aed)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Get started
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create your Blackmount account
              </Typography>
            </Box>

            {/* Value Reminder */}
            <Box sx={{
              mb: 3,
              p: 2.5,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(37, 99, 235, 0.08)',
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary.main}`,
            }}>
              <Typography variant="body2" fontWeight={500} color="primary.dark" sx={{ mb: 0.5 }}>
                ✓ Save Chrome sessions in one click
              </Typography>
              <Typography variant="body2" fontWeight={500} color="primary.dark" sx={{ mb: 0.5 }}>
                ✓ Attach notes and voice memos to projects
              </Typography>
              <Typography variant="body2" fontWeight={500} color="primary.dark">
                ✓ Restore tabs exactly as you left them
              </Typography>
            </Box>

            {/* Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Terms Acceptance - Required for ALL signup methods */}
            <FormControlLabel
              sx={{ mb: 3 }}
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" style={{ color: theme.palette.primary.main }}>
                    Terms
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" target="_blank" style={{ color: theme.palette.primary.main }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />

            {/* Google Sign-up */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignup}
              disabled={loading}
              sx={{
                py: 1.5,
                mb: 3,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {loading ? 'Creating account...' : 'Continue with Google'}
            </Button>

            {/* Divider */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              my: 3,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                height: '1px',
                bgcolor: 'divider',
              },
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                or
              </Typography>
            </Box>

            {/* Email Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Email address"
                  type="email"
                  inputRef={emailRef}
                  required
                  fullWidth
                  autoComplete="email"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />

                <TextField
                  label="Password"
                  type="password"
                  inputRef={passwordRef}
                  required
                  fullWidth
                  autoComplete="new-password"
                  helperText="Choose a strong password with at least 6 characters"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />

                <TextField
                  label="Confirm password"
                  type="password"
                  inputRef={passwordConfirmRef}
                  required
                  fullWidth
                  autoComplete="new-password"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* What Happens Next */}
        <Box sx={{
          mt: 4,
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            What happens after signup?
          </Typography>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Typography variant="body2" color="primary.main" fontWeight={600}>
                1.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check your email for verification link
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Typography variant="body2" color="primary.main" fontWeight={600}>
                2.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Install the Blackmount extension and sign in
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Typography variant="body2" color="primary.main" fontWeight={600}>
                3.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start saving sessions in one click!
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Sign in link */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button
              component={Link}
              to="/login"
              variant="text"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
              }}
            >
              Sign in
            </Button>
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
}

export default Signup;

